import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  LinearProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,

} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  AccountBalance as PayrollIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { payrollService } from '../services/payrollService';
import { employeeService } from '../services/employeeService';
import { Payroll, Employee } from '../types';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currencyutils';
import { payslipPdfService } from '../services/payslipPdfService';
import { companySettingsService } from '../services/companySettingsService';

interface PayrollGroup {
  payPeriod: string;
  startDate: string;
  endDate: string;
  payrolls: Payroll[];
  totalEmployees: number;
  totalGrossPay: number;
  totalNetPay: number;
  paidEmployees: number;
  unpaidEmployees: number;
  allPaid: boolean;
  unpaidPayrolls: Payroll[];
}

const PayrollPage: React.FC = () => {
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState<Payroll | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [payPeriodStart, setPayPeriodStart] = useState<string>('');
  const [payPeriodEnd, setPayPeriodEnd] = useState<string>('');
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [existingPayrollPeriods, setExistingPayrollPeriods] = useState<Set<string>>(new Set());

  // Function to check if payroll exists for a given month
  const checkExistingPayrollPeriods = async () => {
    try {
      const periods = new Set<string>();
      payrolls.forEach(payroll => {
        const startDate = new Date(payroll.pay_period_start);
        const monthKey = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;
        periods.add(monthKey);
      });
      setExistingPayrollPeriods(periods);
    } catch (error) {
      console.error('Error checking existing payroll periods:', error);
    }
  };

  // Function to check if a date is in a month that already has payroll generated
  const isMonthDisabled = (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return existingPayrollPeriods.has(monthKey);
  };

  // Function to get the maximum allowed date (current month)
  const getMaxAllowedDate = (): string => {
    const today = new Date();
    const lastDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return lastDayOfCurrentMonth.toISOString().split('T')[0];
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const payrollsData = await payrollService.getAll();
        const employeesData = await employeeService.getAll();
        setPayrolls(payrollsData);
        setEmployees(employeesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Check existing payroll periods when payrolls data changes
  useEffect(() => {
    if (payrolls.length > 0) {
      checkExistingPayrollPeriods();
    }
  }, [payrolls]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenGenerateDialog = () => {
    // Set default dates to current month
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split('T')[0];
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];

    setPayPeriodStart(firstDay);
    setPayPeriodEnd(lastDay);
    setOpenGenerateDialog(true);
  };

  const handleCloseGenerateDialog = () => {
    setOpenGenerateDialog(false);
    setSelectedEmployee('');
    setPayPeriodStart('');
    setPayPeriodEnd('');
  };

  const handleGeneratePayroll = async () => {
    setIsGenerating(true);
    setGenerationProgress(0);
    setShowSuccessMessage(false);
    
    try {
      if (selectedEmployee) {
        // Generate payroll for a single employee
        setGenerationMessage('Initializing payroll generation...');
        setGenerationProgress(10);
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setGenerationMessage('Processing employee data...');
        setGenerationProgress(30);
        
        const result = await payrollService.generatePayroll(selectedEmployee, payPeriodStart, payPeriodEnd);
        
        setGenerationProgress(70);
        setGenerationMessage('Calculating deductions and taxes...');
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (result) {
          console.log('Payroll generated successfully for employee:', selectedEmployee);
          setGenerationProgress(100);
          setGenerationMessage('Payroll generated successfully!');
        } else {
          console.error('Failed to generate payroll for employee:', selectedEmployee);
          setShowErrorMessage(true);
          setErrorMessage('Failed to generate payroll. Please check if the employee has attendance records for the selected period.');
          return;
        }
      } else {
        // Generate payroll for all employees
        setGenerationMessage('Initializing bulk payroll generation...');
        setGenerationProgress(5);
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setGenerationMessage('Fetching employee data...');
        setGenerationProgress(15);
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setGenerationMessage('Processing attendance records...');
        setGenerationProgress(30);
        
        const results = await payrollService.generatePayrollReport(payPeriodStart, payPeriodEnd);
        
        setGenerationProgress(70);
        setGenerationMessage('Calculating taxes and deductions...');
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setGenerationProgress(90);
        setGenerationMessage('Finalizing payroll records...');
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log(`Payroll generated for ${results.length} employees`);
        if (results.length === 0) {
          setShowErrorMessage(true);
          setErrorMessage('No payroll records were generated. Please check if employees have attendance records for the selected period.');
          return;
        }
        
        setGenerationProgress(100);
        setGenerationMessage(`Successfully generated payroll for ${results.length} employees!`);
      }

      // Show completion message
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowSuccessMessage(true);
      
      // Refresh payroll list
      const payrollsData = await payrollService.getAll();
      setPayrolls(payrollsData);
      
      // Close dialog after showing success
      setTimeout(() => {
        handleCloseGenerateDialog();
        setIsGenerating(false);
        setShowSuccessMessage(false);
        setGenerationProgress(0);
        setGenerationMessage('');
      }, 2000);
      
    } catch (error) {
      console.error('Error generating payroll:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      setShowErrorMessage(true);
      setErrorMessage(`Error generating payroll: ${errorMsg}. Please ensure: 1. Database schema is properly set up 2. Employees have attendance records 3. All required columns exist`);
      setIsGenerating(false);
      setGenerationProgress(0);
      setGenerationMessage('');
    }
  };

  const handleViewPayroll = (payroll: Payroll) => {
    setSelectedPayroll(payroll);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPayroll(null);
  };

  const handleProcessPayment = async (payrollId: string) => {
    try {
      await payrollService.processPayment(payrollId);
      // Refresh payroll list
      const payrollsData = await payrollService.getAll();
      setPayrolls(payrollsData);
    } catch (error) {
      console.error('Error processing payment:', error);
    }
  };

  const handleGenerateAllPayslips = async (payrolls: Payroll[]) => {
    try {
      const companyInfo = await companySettingsService.getCompanyInfoForPayslip();
      payslipPdfService.openAllEmployeePayslipsPDFInNewTab(payrolls, employees, companyInfo);
    } catch (error) {
      console.error('Error generating all payslips:', error);
    }
  };

  const handlePayAllEmployees = async (unpaidPayrolls: Payroll[]) => {
    try {
      // Process payment for all unpaid employees
      await Promise.all(
        unpaidPayrolls.map(payroll => 
          payrollService.processPayment(payroll.id)
        )
      );
      
      // Refresh payroll list
      const payrollsData = await payrollService.getAll();
      setPayrolls(payrollsData);
    } catch (error) {
      console.error('Error processing payments for all employees:', error);
    }
  };

  // Get employee name by ID
  const getEmployeeName = (employeeId: string): string => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
  };

// Group payrolls by pay period
  const groupPayrollsByPeriod = (payrollData: any[]): PayrollGroup[] => {
    const groups = new Map<string, PayrollGroup>();
    
    // Filter payrolls based on search term
    const filteredPayrolls = payrollData.filter(payroll => {
      if (!searchTerm) return true;
      const employeeName = getEmployeeName(payroll.employee_id).toLowerCase();
      return employeeName.includes(searchTerm.toLowerCase());
    });
    
    filteredPayrolls.forEach(payroll => {
      const periodKey = `${payroll.pay_period_start}_${payroll.pay_period_end}`;
      
      if (!groups.has(periodKey)) {
        groups.set(periodKey, {
          payPeriod: `${formatDate(payroll.pay_period_start)} - ${formatDate(payroll.pay_period_end)}`,
          startDate: payroll.pay_period_start,
          endDate: payroll.pay_period_end,
          payrolls: [],
          totalEmployees: 0,
          totalGrossPay: 0,
          totalNetPay: 0,
          paidEmployees: 0,
          unpaidEmployees: 0,
          allPaid: false,
          unpaidPayrolls: [],
        });
      }
      
      const group = groups.get(periodKey)!;
      group.payrolls.push(payroll);
      group.totalEmployees = group.payrolls.length;
      group.totalGrossPay += payroll.gross_pay || payroll.basic_salary;
      group.totalNetPay += payroll.net_pay;
      
      // Track payment status
      if (payroll.payment_status === 'paid') {
        group.paidEmployees++;
      } else {
        group.unpaidEmployees++;
        group.unpaidPayrolls.push(payroll);
      }
      
      // Update allPaid status
      group.allPaid = group.unpaidEmployees === 0;
    });
    
    // Sort by start date (most recent first)
    return Array.from(groups.values()).sort((a, b) => 
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  };

  // Handle accordion expansion
  const handleAccordionChange = (periodKey: string) => {
    const newExpanded = new Set(expandedPeriods);
    if (newExpanded.has(periodKey)) {
      newExpanded.delete(periodKey);
    } else {
      newExpanded.add(periodKey);
    }
    setExpandedPeriods(newExpanded);
  };

  // Group payrolls by pay period
  const groupedPayrolls = groupPayrollsByPeriod(payrolls);
  
  // Pagination for groups
  const paginatedGroups = groupedPayrolls.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isGenerating) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: 3,
        px: 4
      }}>
        <Box sx={{
          background: showErrorMessage 
            ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%)'
            : showSuccessMessage 
            ? 'linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)'
            : 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #1e40af 100%)',
          color: 'white',
          p: 4,

          minWidth: 400,
          maxWidth: 600,
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(37, 99, 235, 0.2), 0 8px 16px rgba(124, 58, 237, 0.15)',
        }}>
          <PayrollIcon sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {showErrorMessage ? '❌ Payroll Generation Failed' : 
             showSuccessMessage ? '✅ Payroll Generated Successfully!' : 
             'Generating Payroll'}
          </Typography>
          
          {showErrorMessage && (
            <>
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                {errorMessage}
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => {
                  setIsGenerating(false);
                  setShowErrorMessage(false);
                  setErrorMessage('');
                  setGenerationProgress(0);
                  setGenerationMessage('');
                }}
                sx={{ 
                  mt: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
                }}
              >
                Try Again
              </Button>
            </>
          )}
          
          {!showSuccessMessage && !showErrorMessage && (
            <>
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                {generationMessage}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={generationProgress} 
                  sx={{
                    height: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                    }
                  }}
                />
              </Box>
              
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {generationProgress}%
              </Typography>
            </>
          )}
          
          {showSuccessMessage && (
            <>
              <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                {generationMessage}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Redirecting to payroll dashboard...
              </Typography>
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #1e40af 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(37, 99, 235, 0.2), 0 8px 16px rgba(124, 58, 237, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            opacity: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4,
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              Payroll Management
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.9)' }}>
              Generate and manage employee payrolls with comprehensive tracking
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenGenerateDialog}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Generate Payroll
          </Button>
        </Box>
      </Box>

      {/* Payroll Groups */}
      <Box sx={{ mb: 3 }}>
        {paginatedGroups.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No payroll records found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Generate payroll records to see them grouped by pay period
            </Typography>
          </Paper>
        ) : (
          paginatedGroups.map((group) => {
            const periodKey = `${group.startDate}_${group.endDate}`;
            const isExpanded = expandedPeriods.has(periodKey);
            
            return (
              <Accordion
                key={periodKey}
                expanded={isExpanded}
                onChange={() => handleAccordionChange(periodKey)}
                sx={{
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)',
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '16px 0',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        📅 {group.payPeriod}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {group.totalEmployees} employee{group.totalEmployees !== 1 ? 's' : ''} • Total Gross: {formatCurrency(group.totalGrossPay)} • Total Net: {formatCurrency(group.totalNetPay)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {group.allPaid ? (
                          <span style={{ color: '#059669' }}>✅ All employees paid</span>
                        ) : (
                          <span style={{ color: '#dc2626' }}>⚠️ {group.unpaidEmployees} employee{group.unpaidEmployees !== 1 ? 's' : ''} unpaid</span>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={`${group.totalEmployees} Total`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={formatCurrency(group.totalNetPay)}
                          size="small"
                          color="success"
                          variant="filled"
                        />
                        <Chip
                          label={`${group.paidEmployees} Paid`}
                          size="small"
                          color="success"
                          variant={group.allPaid ? "filled" : "outlined"}
                        />
                        {group.unpaidEmployees > 0 && (
                          <Chip
                            label={`${group.unpaidEmployees} Unpaid`}
                            size="small"
                            color="error"
                            variant="filled"
                          />
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<PrintIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateAllPayslips(group.payrolls);
                          }}
                          sx={{
                            fontSize: '0.75rem',
                            padding: '4px 8px',
                            minWidth: 'auto',

                            textTransform: 'none'
                          }}
                        >
                          Generate All Payslips
                        </Button>
                        {group.unpaidEmployees > 0 && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<PaymentIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePayAllEmployees(group.payrolls.filter(p => p.payment_status !== 'paid'));
                            }}
                            sx={{
                              fontSize: '0.75rem',
                              padding: '4px 8px',
                              minWidth: 'auto',

                              textTransform: 'none',
                              ml: 1
                            }}
                          >
                            Pay All ({group.unpaidEmployees})
                          </Button>
                        )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {/* Search Box */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Search employees in this payroll period..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ color: '#64748b', mr: 1 }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',

                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2563eb',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2563eb',
                          },
                        },
                      }}
                    />
                  </Box>
                  {/* Unpaid Employees Alert */}
                  {group.unpaidEmployees > 0 && (
                    <Box sx={{ 
                      backgroundColor: '#fef2f2', 
                      borderLeft: '4px solid #dc2626', 
                      p: 2, 
                      m: 2, 
 
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#dc2626', mb: 1 }}>
                        ⚠️ Unpaid Employees ({group.unpaidEmployees})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {group.unpaidPayrolls.map((unpaidPayroll) => (
                          <Chip
                            key={unpaidPayroll.id}
                            label={getEmployeeName(unpaidPayroll.employee_id)}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ 
                              backgroundColor: '#ffffff',
                              '&:hover': {
                                backgroundColor: '#fee2e2'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Hours Worked</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Gross Pay</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Total Deductions</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Net Pay</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Payment Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.payrolls.map((payroll, index) => (
                          <TableRow 
                            key={payroll.id}
                            sx={{
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                              '&:hover': {
                                backgroundColor: '#e2e8f0',
                              },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {getEmployeeName(payroll.employee_id)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {payroll.total_hours_worked ? (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {payroll.total_hours_worked.toFixed(1)}h
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    R: {payroll.regular_hours?.toFixed(1) || '0'}h | OT: {payroll.overtime_hours?.toFixed(1) || '0'}h
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#059669' }}>
                                {formatCurrency(payroll.gross_pay || payroll.basic_salary)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#dc2626' }}>
                                {formatCurrency(payroll.total_deductions)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {formatCurrency(payroll.net_pay)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(payroll.payment_date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={payroll.payment_status}
                                color={
                                  payroll.payment_status === 'paid'
                                    ? 'success'
                                    : payroll.payment_status === 'pending'
                                    ? 'warning'
                                    : 'error'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  startIcon={<ViewIcon />}
                                  onClick={() => handleViewPayroll(payroll)}
                                  variant="outlined"
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  View
                                </Button>
                                {payroll.payment_status === 'pending' && (
                                  <Button
                                    size="small"
                                    startIcon={<PaymentIcon />}
                                    onClick={() => handleProcessPayment(payroll.id)}
                                    color="primary"
                                    variant="contained"
                                    sx={{ minWidth: 'auto', px: 1 }}
                                  >
                                    Pay
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>

      {/* Pagination */}
      {groupedPayrolls.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={groupedPayrolls.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count} pay periods`
            }
          />
        </Paper>
      )}

      {/* Generate Payroll Dialog */}
      <Dialog 
        open={openGenerateDialog} 
        onClose={handleCloseGenerateDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 16px 32px rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(20px)',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden',
            transform: 'scale(1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }
        }}
        TransitionProps={{
          timeout: 400,
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
          },
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%, #667eea 200%)',
          backgroundSize: '200% 200%',
          animation: 'gradientShift 6s ease infinite',
          color: 'white',
          textAlign: 'center',
          py: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            animation: 'shimmer 3s infinite',
          },
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
          '@keyframes shimmer': {
            '0%': { left: '-100%' },
            '100%': { left: '100%' },
          },
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 2,
            position: 'relative',
            zIndex: 1,
          }}>
            <PayrollIcon sx={{ 
              fontSize: 40, 
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
              animation: 'iconFloat 3s ease-in-out infinite',
              '@keyframes iconFloat': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-4px)' },
              },
            }} />
            <Typography variant="h4" sx={{ 
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              letterSpacing: '0.5px',
            }}>
              Generate Payroll
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ 
            mt: 2, 
            opacity: 0.95,
            fontWeight: 500,
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            position: 'relative',
            zIndex: 1,
          }}>
            Create payroll records for your employees
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: 5,
          background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23f0f0f0" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="1.5"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
            pointerEvents: 'none',
          },
        }}>
          {/* Information Banner */}
          <Paper sx={{ 
            p: 4, 
            mb: 4, 
            background: 'linear-gradient(135deg, rgba(227, 242, 253, 0.8) 0%, rgba(243, 229, 245, 0.8) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(225, 190, 231, 0.3)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <InfoIcon sx={{ color: '#1976d2' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Payroll Generation Guidelines
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Ensure all employee attendance records are complete for the selected period
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Payroll will be generated based on recorded work hours and employee salary settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Duplicate payroll records for the same period will be automatically prevented
            </Typography>
          </Paper>

          {/* Employee Selection Section */}
          <Paper sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 3, 
            border: '1px solid rgba(224, 224, 224, 0.3)',
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <PeopleIcon sx={{ color: '#4caf50' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                Employee Selection
              </Typography>
            </Box>
            <FormControl fullWidth>
              <InputLabel shrink sx={{ fontWeight: 500 }}>Select Employee(s)</InputLabel>
              <Select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value as string)}
                label="Select Employee(s)"
                displayEmpty
                notched

                renderValue={(selected) => {
                  if (selected === '') {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                        <span>All Employees ({employees.length} total)</span>
                      </Box>
                    );
                  }
                  const employee = employees.find(emp => emp.id === selected);
                  return employee ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                      <span>{`${employee.first_name} ${employee.last_name}`}</span>
                    </Box>
                  ) : '';
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PeopleIcon sx={{ color: '#4caf50' }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        All Employees
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generate payroll for all {employees.length} employees
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: 14,
                        fontWeight: 600
                      }}>
                        {employee.first_name.charAt(0)}{employee.last_name.charAt(0)}
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {`${employee.first_name} ${employee.last_name}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.position} • {employee.department}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {/* Pay Period Section */}
          <Paper sx={{ 
            p: 4, 
            borderRadius: 3, 
            border: '1px solid rgba(224, 224, 224, 0.3)',
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CalendarIcon sx={{ color: '#ff9800' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#f57c00' }}>
                Pay Period Configuration
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Pay Period Start Date"
                    type="date"
                    fullWidth
                    value={payPeriodStart}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      if (isMonthDisabled(selectedDate)) {
                        alert('Payroll for this month has already been generated. Please select a different month.');
                        return;
                      }
                      setPayPeriodStart(selectedDate);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: getMaxAllowedDate()
                    }}
                    InputProps={{
                      startAdornment: (
                        <CalendarIcon sx={{ mr: 1, color: '#ff9800' }} />
                      ),
                    }}
                    helperText={isMonthDisabled(payPeriodStart) ? "⚠️ Payroll already generated for this month" : "Select the start date of the pay period"}
                    error={isMonthDisabled(payPeriodStart)}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Pay Period End Date"
                    type="date"
                    fullWidth
                    value={payPeriodEnd}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      if (isMonthDisabled(selectedDate)) {
                        alert('Payroll for this month has already been generated. Please select a different month.');
                        return;
                      }
                      setPayPeriodEnd(selectedDate);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: getMaxAllowedDate()
                    }}
                    InputProps={{
                      startAdornment: (
                        <CalendarIcon sx={{ mr: 1, color: '#ff9800' }} />
                      ),
                    }}
                    helperText={isMonthDisabled(payPeriodEnd) ? "⚠️ Payroll already generated for this month" : "Select the end date of the pay period"}
                    error={isMonthDisabled(payPeriodEnd)}
                  />
                </Box>
              </Box>
            
            {/* Period Summary */}
            {payPeriodStart && payPeriodEnd && (
              <Paper sx={{ 
                mt: 4, 
                p: 3, 
                background: (isMonthDisabled(payPeriodStart) || isMonthDisabled(payPeriodEnd)) 
                  ? 'linear-gradient(135deg, rgba(255, 235, 238, 0.8) 0%, rgba(255, 205, 210, 0.8) 100%)' 
                  : 'linear-gradient(135deg, rgba(255, 243, 224, 0.8) 0%, rgba(252, 228, 236, 0.8) 100%)',
                backdropFilter: 'blur(10px)',
                border: (isMonthDisabled(payPeriodStart) || isMonthDisabled(payPeriodEnd)) 
                  ? '1px solid rgba(244, 67, 54, 0.3)' 
                  : '1px solid rgba(255, 204, 2, 0.3)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                zIndex: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {(isMonthDisabled(payPeriodStart) || isMonthDisabled(payPeriodEnd)) ? (
                    <>
                      <Box sx={{ color: '#f44336', fontSize: 20 }}>⚠️</Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                        Invalid Pay Period
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        Pay Period Summary
                      </Typography>
                    </>
                  )}
                </Box>
                {(isMonthDisabled(payPeriodStart) || isMonthDisabled(payPeriodEnd)) ? (
                  <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                    ⚠️ Payroll has already been generated for the selected month(s). Please choose a different period.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Duration:</strong> {formatDate(payPeriodStart)} to {formatDate(payPeriodEnd)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Target:</strong> {selectedEmployee ? '1 employee' : `${employees.length} employees`}
                    </Typography>
                  </>
                )}
              </Paper>
            )}
          </Paper>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 4, 
          background: 'linear-gradient(135deg, rgba(248, 249, 250, 0.9) 0%, rgba(240, 242, 247, 0.9) 100%)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(224, 224, 224, 0.3)',
          gap: 3,
          position: 'relative',
          zIndex: 1,
        }}>
          <Button 
            onClick={handleCloseGenerateDialog} 
            disabled={loading}
            variant="outlined"
            sx={{ 
              borderColor: 'rgba(102, 126, 234, 0.3)',
              color: '#667eea',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              px: 4,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#667eea',
                background: 'rgba(102, 126, 234, 0.1)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
              },
              '&:disabled': {
                borderColor: 'rgba(158, 158, 158, 0.3)',
                color: '#9e9e9e',
                background: 'rgba(224, 224, 224, 0.5)',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleGeneratePayroll} 
            variant="contained"
            disabled={loading || !payPeriodStart || !payPeriodEnd || isMonthDisabled(payPeriodStart) || isMonthDisabled(payPeriodEnd)}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PayrollIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundSize: '200% 200%',
              animation: loading ? 'none' : 'gradientShift 3s ease infinite',
              px: 5,
              py: 2,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                transition: 'left 0.5s',
              },
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 32px rgba(102, 126, 234, 0.6)',
                '&::before': {
                  left: '100%',
                },
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                color: '#9e9e9e',
                boxShadow: 'none',
                transform: 'none',
              },
              '@keyframes gradientShift': {
                '0%': { backgroundPosition: '0% 50%' },
                '50%': { backgroundPosition: '100% 50%' },
                '100%': { backgroundPosition: '0% 50%' },
              },
            }}
          >
            {loading ? 'Generating Payroll...' : 'Generate Payroll'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Payroll Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #1d4ed8 100%)',
            color: 'white',
            py: 3,
            px: 4,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              opacity: 0.4,
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Payroll Details
            </Typography>
            {selectedPayroll && (
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                {getEmployeeName(selectedPayroll.employee_id)}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedPayroll && (
            <Box>
              {/* Header Section */}
              <Box sx={{ 
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                p: 4,
                borderBottom: '1px solid #e2e8f0'
              }}>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Pay Period
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatDate(selectedPayroll.pay_period_start)} - {formatDate(selectedPayroll.pay_period_end)}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Payment Status
                    </Typography>
                    <Chip
                      label={selectedPayroll.payment_status}
                      color={
                        selectedPayroll.payment_status === 'paid'
                          ? 'success'
                          : selectedPayroll.payment_status === 'pending'
                          ? 'warning'
                          : 'error'
                      }
                      sx={{ 
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        fontSize: '0.875rem',
                        height: 32
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ p: 4 }}>
                <Grid container spacing={4}>
                  {/* Time & Hours Section */}
                  <Grid size={12}>
                    <Paper sx={{ 
                      p: 3, 
 
                      background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                      border: '1px solid #93c5fd'
                    }}>
                      <Typography variant="h6" sx={{ 
                        mb: 3, 
                        color: '#1e40af', 
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        ⏰ Time & Hours
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e40af', mb: 1 }}>
                              {selectedPayroll.total_hours_worked ? `${selectedPayroll.total_hours_worked.toFixed(1)}h` : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Total Hours
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#059669', mb: 1 }}>
                              {selectedPayroll.regular_hours ? `${selectedPayroll.regular_hours.toFixed(1)}h` : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Regular Hours
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={4}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#dc2626', mb: 1 }}>
                              {selectedPayroll.overtime_hours ? `${selectedPayroll.overtime_hours.toFixed(1)}h` : '0h'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Overtime Hours
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Earnings Section */}
                  <Grid size={6}>
                    <Paper sx={{ 
                      p: 3, 
 
                      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                      border: '1px solid #6ee7b7',
                      height: '100%'
                    }}>
                      <Typography variant="h6" sx={{ 
                        mb: 3, 
                        color: '#059669', 
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        💰 Earnings
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Basic Salary</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#059669' }}>
                            {formatCurrency(selectedPayroll.basic_salary)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Overtime Pay</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#059669' }}>
                            {formatCurrency(selectedPayroll.overtime_pay)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Bonuses</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#059669' }}>
                            {formatCurrency(selectedPayroll.bonuses)}
                          </Typography>
                        </Box>
                        {selectedPayroll.thirteenth_month_pay && selectedPayroll.thirteenth_month_pay > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>13th Month Pay</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#059669' }}>
                              {formatCurrency(selectedPayroll.thirteenth_month_pay)}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          pt: 2,
                          borderTop: '2px solid #059669',
                          mt: 1
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#059669' }}>Gross Pay</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#059669' }}>
                            {formatCurrency(selectedPayroll.gross_pay || selectedPayroll.basic_salary)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Deductions Section */}
                  <Grid size={6}>
                    <Paper sx={{ 
                      p: 3, 
 
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      border: '1px solid #f59e0b',
                      height: '100%'
                    }}>
                      <Typography variant="h6" sx={{ 
                        mb: 3, 
                        color: '#d97706', 
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        🏛️ Government Contributions
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>SSS</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#d97706' }}>
                            {formatCurrency(selectedPayroll.sss_contribution || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>PhilHealth</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#d97706' }}>
                            {formatCurrency(selectedPayroll.philhealth_contribution || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Pag-IBIG</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#d97706' }}>
                            {formatCurrency(selectedPayroll.pagibig_contribution || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Withholding Tax</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#d97706' }}>
                            {formatCurrency(selectedPayroll.withholding_tax)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Other Deductions</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#d97706' }}>
                            {formatCurrency(selectedPayroll.other_deductions || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          pt: 2,
                          borderTop: '2px solid #d97706',
                          mt: 1
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#d97706' }}>Total Deductions</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#d97706' }}>
                            {formatCurrency(selectedPayroll.total_deductions)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Net Pay Section */}
                  <Grid size={12}>
                    <Paper sx={{ 
                      p: 4, 
 
                      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                      color: 'white',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                        opacity: 0.4,
                      },
                    }}>
                      <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, opacity: 0.9 }}>
                          💵 Net Pay
                        </Typography>
                        <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>
                          {formatCurrency(selectedPayroll.net_pay)}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.8 }}>
                          Final Amount After Deductions
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Payment Information */}
                  <Grid size={12}>
                    <Paper sx={{ 
                      p: 3, 
 
                      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                      border: '1px solid #0ea5e9'
                    }}>
                      <Typography variant="h6" sx={{ 
                        mb: 3, 
                        color: '#0369a1', 
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        📅 Payment Information
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Payment Date
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0369a1' }}>
                            {formatDate(selectedPayroll.payment_date)}
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Notes
                          </Typography>
                          <Typography variant="body1" sx={{ fontStyle: selectedPayroll.notes ? 'normal' : 'italic', color: selectedPayroll.notes ? 'text.primary' : 'text.secondary' }}>
                            {selectedPayroll.notes || 'No additional notes'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderTop: '1px solid #e2e8f0',
          gap: 2
        }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: 'text.secondary',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Close
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => {
              if (selectedPayroll) {
                const employee = employees.find(emp => emp.id === selectedPayroll.employee_id);
                if (employee) {
                  payslipPdfService.openPayslipPDFInNewTab({
                    payroll: selectedPayroll,
                    employee: employee,
                    companyInfo: {
                      name: 'Your Company Name',
                      address: 'Company Address, City, Province',
                      tin: 'TIN: 000-000-000-000',
                      sssNumber: 'SSS: 00-0000000-0',
                      philHealthNumber: 'PhilHealth: 00-000000000-0',
                      pagIbigNumber: 'Pag-IBIG: 0000-0000-0000'
                    }
                  });
                } else {
                  console.error('Employee not found for payroll');
                }
              }
            }}
            sx={{
              borderColor: '#3b82f6',
              color: '#3b82f6',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#1e40af',
                backgroundColor: 'rgba(59, 130, 246, 0.04)'
              }
            }}
          >
            Print Slip
          </Button>
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={() => {
              if (selectedPayroll) {
                handleProcessPayment(selectedPayroll.id);
                handleCloseDialog();
              }
            }}
            disabled={selectedPayroll?.payment_status === 'paid'}
            sx={{
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                boxShadow: '0 6px 16px rgba(5, 150, 105, 0.4)',
              },
              '&:disabled': {
                background: '#e5e7eb',
                color: '#9ca3af'
              }
            }}
          >
            Process Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollPage;